import React from "react";
import { useCart } from "../../context/CartContext";
import { useNavigate } from "react-router-dom";

const formatCurrency = new Intl.NumberFormat("es-MX", {
  style: "currency",
  currency: "MXN",
  minimumFractionDigits: 2,
});

const CartDrawer = () => {
  const {
    items,
    isOpen,
    closeCart,
    updateQuantity,
    removeItem,
    subtotal,
    shipping,
    total,
    totalUnits,
    minimumOrderUnits,
    isMinimumOrderMet,
    shippingConfig,
    iva,
  } = useCart();
  const navigate = useNavigate();

  if (!isOpen) return null;

  const threshold = shippingConfig?.threshold ?? 499;
  const shippingLabel = subtotal >= threshold ? "GRATIS" : formatCurrency.format(shipping);
  const missingUnits = Math.max(0, minimumOrderUnits - totalUnits);

  return (
    <div className="fixed inset-0 z-[1100]">
      <div
        className="absolute inset-0 bg-black/60"
        role="button"
        tabIndex={-1}
        onClick={closeCart}
        onKeyDown={(event) => {
          if (event.key === "Escape") closeCart();
        }}
      />
      <div className="absolute right-4 md:right-8 top-6 bottom-6 w-[340px] sm:w-[380px] md:w-[420px] bg-[#0f171a] text-white rounded-2xl shadow-2xl flex flex-col border border-white/10">
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
          <h2 className="text-lg font-bold tracking-wide">TU CARRITO</h2>
          <button
            type="button"
            onClick={closeCart}
            className="text-green-400 text-2xl font-bold leading-none"
            aria-label="Cerrar"
          >
            X
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {items.length === 0 ? (
            <div className="text-sm text-white/70">Tu carrito está vacío.</div>
          ) : (
            items.map((item) => (
              <div key={item.key} className="border-b border-white/10 pb-4">
                <div className="flex items-start justify-between gap-3">
                  <button
                    type="button"
                    onClick={() => removeItem(item.key)}
                    className="text-white/60 hover:text-white transition"
                    aria-label="Eliminar"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="w-4 h-4"
                    >
                      <path d="M3 6h18" />
                      <path d="M8 6V4h8v2" />
                      <path d="M6 6l1 14h10l1-14" />
                      <path d="M10 11v6" />
                      <path d="M14 11v6" />
                    </svg>
                  </button>
                  <div className="flex-1">
                    <div className="text-sm font-semibold">{item.name}</div>
                    <div className="text-xs text-white/60">
                      Unitario: {formatCurrency.format(item.unitPrice || 0)}
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-end mt-3">
                  <div className="flex items-center border border-white/30 rounded-md overflow-hidden">
                    <button
                      type="button"
                      onClick={() => updateQuantity(item.key, item.quantity - 1)}
                      disabled={item.quantity <= 1}
                      className="px-3 py-1 text-white hover:bg-white/10 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      -
                    </button>
                    <span className="px-4 py-1 text-sm">{item.quantity}</span>
                    <button
                      type="button"
                      onClick={() => updateQuantity(item.key, item.quantity + 1)}
                      className="px-3 py-1 text-white hover:bg-white/10"
                    >
                      +
                    </button>
                  </div>
                  <div className="ml-3 text-sm font-semibold">
                    {formatCurrency.format((item.unitPrice || 0) * item.quantity)}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        <div className="px-5 py-4 border-t border-white/10 space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span>IVA:</span>
            <span>{formatCurrency.format(iva)}</span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span>Envío:</span>
            <span>{items.length ? shippingLabel : "--"}</span>
          </div>
          <div className="flex items-center justify-between text-base font-semibold">
            <span>Subtotal:</span>
            <span>{formatCurrency.format(subtotal)}</span>
          </div>
          <div className="flex items-center justify-between text-base font-semibold">
            <span>Total:</span>
            <span>{formatCurrency.format(total)}</span>
          </div>
          <div className="text-xs text-white/60">Precios incluyen IVA.</div>
          {!isMinimumOrderMet && items.length > 0 && (
            <div className="text-xs font-semibold text-amber-300">
              Pedido mínimo: {minimumOrderUnits} unidades. Agrega {missingUnits} más para pagar.
            </div>
          )}
          <button
            type="button"
            onClick={() => {
              closeCart();
              navigate("/ecommerce/checkout");
            }}
            disabled={!items.length || !isMinimumOrderMet}
            className="w-full mt-3 bg-primary-green text-white font-bold py-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
          >
            PAGAR
          </button>
        </div>
      </div>
    </div>
  );
};

export default CartDrawer;
